// =============================================================================
// CC-AUDIT-43 L2 — DAL desktop = wizard PARTAGÉ (MobileDALWizard) embarqué
// -----------------------------------------------------------------------------
// L'onglet DAL desktop rend désormais le MÊME composant que la PWA mobile
// (source unique). Ici on se contente de :
//   1. charger les données DAL existantes via le GET DAL desktop
//      (GET /api/metier/tef/dossiers/{id}/dal → infos_dal dont `zones_mobile`,
//      `selected_zones`, `besoins`, `intervenant`, + `canonical`),
//   2. les passer en `initialData` au wizard (REPRISE de la saisie),
//   3. rendre le wizard en mode `embedded` (carte bornée, pas plein-écran).
//
// Auth : la session desktop (session_token) suffit — usePatchDAL/upload/save
// utilisent `getDalAuthToken()` (session_token en fallback) et le backend
// `_verify_mobile_token` accepte une session desktop (tenant du dossier ==
// current_tenant). Aucun mobile_token requis côté desktop.
// =============================================================================
import { useState, useEffect, useCallback } from "react";
import { Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import MobileDALWizard from "../../../components/dal/MobileDALWizard";
import { formatClientFullName } from "../../../utils/clientFormat";
import { API, getAuthHeaders } from "./helpers";

export function DesktopDALWizard({ dossier, dossierId, tenantId, onUpdate }) {
  const [initialData, setInitialData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDal = useCallback(async () => {
    if (!dossierId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${API}/api/metier/tef/dossiers/${dossierId}/dal`,
        { headers: getAuthHeaders(tenantId), credentials: "include" }
      );
      if (!res.ok) {
        setError(`HTTP ${res.status}`);
        setInitialData({}); // wizard reste utilisable (saisie vierge)
      } else {
        const data = await res.json();
        // La réponse est déjà au format `infos_dal` attendu par buildInitial*
        // (zones_mobile / selected_zones / besoins / intervenant).
        setInitialData(data || {});
      }
    } catch (e) {
      setError(e?.message || "réseau");
      setInitialData({});
    } finally {
      setLoading(false);
    }
  }, [dossierId, tenantId]);

  useEffect(() => { loadDal(); }, [loadDal]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-[var(--oh-text-secondary)]" data-testid="desktop-dal-loading">
        <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Chargement du DAL…
      </div>
    );
  }

  const dossierInfo = {
    reference: dossier?.reference || dossier?.référence,
    type_projet: dossier?.type_projet,
    logement: dossier?.logement || dossier?.infos_logement,
    surface_habitable: dossier?.surface_habitable ?? dossier?.logement?.surface_habitable,
    nb_pieces: dossier?.nb_pieces ?? dossier?.logement?.nb_pieces,
    // Point 4 — âge MPA dérivé serveur depuis date_naissance (source unique),
    // consommé en repli par le wizard pour pré-remplir l'âge en lecture seule.
    eligibilite_mpa_age: dossier?.eligibilite_mpa?.age ?? null,
  };

  return (
    <div data-testid="desktop-dal-wizard-wrapper">
      {error && (
        <div className="mb-3 flex items-center justify-between rounded-lg px-3 py-2 text-xs"
             style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.25)", color: "#fca5a5" }}>
          <span>DAL : échec du chargement ({error}). Saisie possible, mais vérifiez la reprise.</span>
          <button onClick={loadDal} className="flex items-center gap-1 underline">
            <RefreshCw className="w-3 h-3" /> Réessayer
          </button>
        </div>
      )}
      <MobileDALWizard
        embedded
        dossierId={dossierId}
        tenantId={tenantId}
        clientName={formatClientFullName(dossier?.client) || dossier?.client_nom || "Client"}
        clientInfo={dossier?.client || {}}
        dossierInfo={dossierInfo}
        initialData={initialData || {}}
        visiteurAmo={dossier?.visiteur_amo || ""}
        onComplete={() => {
          toast.success("DAL enregistré");
          onUpdate?.();
          loadDal();
        }}
      />
    </div>
  );
}

export default DesktopDALWizard;
